import React, { useEffect, useState } from "react";
import Layout, { PageHeader } from "@/components/Layout";
import { api, formatApiError } from "@/lib/api";
import { Plus, Trash, Users, CopySimple } from "@phosphor-icons/react";

const EMPTY = { email: "", name: "", password: "", client_id: "" };

export default function PortalUsers() {
  const [items, setItems] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [error, setError] = useState("");
  const [lastCreated, setLastCreated] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [u, c] = await Promise.all([
        api.get("/client-portal-users"),
        api.get("/clients"),
      ]);
      setItems(u.data); setClients(c.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    setError("");
    try {
      const { data } = await api.post("/client-portal-users", form);
      setLastCreated({ ...data, password: form.password });
      setForm(EMPTY);
      setCreating(false);
      await load();
    } catch (e) { setError(formatApiError(e.response?.data?.detail) || e.message); }
  };

  const remove = async (id) => {
    if (!window.confirm("Revoke this portal user's access?")) return;
    await api.delete(`/client-portal-users/${id}`); await load();
  };

  return (
    <Layout>
      <PageHeader
        title="Client portal users"
        subtitle="Give client POCs a secured, read-only window into their data"
        actions={
          <button onClick={() => { setCreating(true); setForm(EMPTY); setError(""); setLastCreated(null); }} data-testid="portal-users-new-btn"
            className="inline-flex items-center gap-2 px-3 py-1.5 text-sm bg-brand-cyan text-brand-navy hover:bg-brand-cyan/90 rounded-md font-semibold transition-colors">
            <Plus size={14} weight="bold" /> Invite POC
          </button>
        }
      />
      <div className="p-6 space-y-6">
        {lastCreated && (
          <div className="sw-card p-5 border-brand-cyan/40" data-testid="last-created-creds">
            <div className="text-xs font-semibold uppercase tracking-[0.15em] text-brand-cyan mb-2">Portal credentials created — share securely</div>
            <div className="grid sm:grid-cols-3 gap-3 text-sm">
              <div>
                <div className="text-[10px] uppercase tracking-[0.15em] text-slate-400">Login URL</div>
                <div className="text-white break-all">{window.location.origin}/login</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.15em] text-slate-400">Email</div>
                <div className="text-white">{lastCreated.email}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.15em] text-slate-400">Password</div>
                <div className="text-white">{lastCreated.password}</div>
              </div>
            </div>
            <button onClick={() => {
              navigator.clipboard.writeText(
                `SentryWatch portal\nURL: ${window.location.origin}/login\nEmail: ${lastCreated.email}\nPassword: ${lastCreated.password}`
              );
            }}
              className="mt-3 text-xs inline-flex items-center gap-1.5 text-brand-cyan hover:text-white">
              <CopySimple size={12} weight="bold" /> Copy credentials
            </button>
          </div>
        )}

        {loading ? (
          <div className="text-sm text-slate-500">loading…</div>
        ) : items.length === 0 ? (
          <div className="sw-card border-dashed p-16 text-center">
            <Users size={36} weight="duotone" className="text-brand-cyan mx-auto mb-3" />
            <h3 className="font-display text-lg font-bold tracking-tight mb-1">No client POC accounts yet</h3>
            <p className="text-sm text-slate-400">Invite a client POC to share a read-only portal of their backups and monitors.</p>
          </div>
        ) : (
          <div className="sw-card overflow-hidden">
            <table className="w-full text-sm" data-testid="portal-users-table">
              <thead className="bg-background-deep/60">
                <tr className="text-[10px] uppercase tracking-[0.15em] text-slate-400">
                  <th className="text-left py-3 px-4">Name</th>
                  <th className="text-left py-3 px-4">Email</th>
                  <th className="text-left py-3 px-4">Client</th>
                  <th className="text-left py-3 px-4">Created</th>
                  <th className="text-right py-3 px-4">Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map((u) => (
                  <tr key={u.id} className="border-t border-border hover:bg-white/5">
                    <td className="py-3 px-4 text-white">{u.name}</td>
                    <td className="py-3 px-4 text-xs text-slate-300">{u.email}</td>
                    <td className="py-3 px-4 text-slate-300">{u.client_name}</td>
                    <td className="py-3 px-4 text-xs text-slate-400">{new Date(u.created_at).toLocaleDateString()}</td>
                    <td className="py-3 px-4 text-right">
                      <button onClick={() => remove(u.id)} data-testid={`portal-user-delete-${u.id}`}
                        className="p-1.5 text-slate-300 hover:text-red-400"><Trash size={14} /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {creating && (
        <div className="fixed inset-0 bg-black/70 z-30 flex items-center justify-center p-6" onClick={() => setCreating(false)}>
          <form onSubmit={save} onClick={(e) => e.stopPropagation()}
            className="sw-card w-full max-w-md p-6 space-y-3" data-testid="portal-user-form">
            <h3 className="font-display text-lg font-bold tracking-tight">Invite client POC</h3>
            <p className="text-xs text-slate-400">They&apos;ll sign in at /login and see only their assigned client&apos;s monitors and backups (read-only).</p>
            <FieldP label="Full name" required>
              <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="portal-user-name-input" />
            </FieldP>
            <FieldP label="Email" required>
              <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="portal-user-email-input" />
            </FieldP>
            <FieldP label="Initial password" required>
              <input required minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="portal-user-password-input" />
            </FieldP>
            <FieldP label="Scope to client" required>
              <select required value={form.client_id} onChange={(e) => setForm({ ...form, client_id: e.target.value })} data-testid="portal-user-client-select">
                <option value="">— select a client —</option>
                {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </FieldP>
            {error && <div className="text-xs text-red-400">{error}</div>}
            <div className="flex justify-end gap-2 pt-2 border-t border-border">
              <button type="button" onClick={() => setCreating(false)} data-testid="portal-user-cancel-btn"
                className="px-3 py-1.5 text-sm border border-border hover:bg-white/5 rounded-md">Cancel</button>
              <button type="submit" data-testid="portal-user-save-btn"
                className="px-3 py-1.5 text-sm bg-brand-cyan text-brand-navy hover:bg-brand-cyan/90 rounded-md font-semibold">Create</button>
            </div>
          </form>
        </div>
      )}
    </Layout>
  );
}

function FieldP({ label, children, required }) {
  return (
    <div>
      <label className="block text-xs font-semibold uppercase tracking-[0.15em] text-slate-400 mb-1.5">
        {label}{required && <span className="text-red-400 ml-0.5">*</span>}
      </label>
      {React.cloneElement(children, {
        className: "w-full bg-background-deep border border-border rounded px-3 py-2 text-sm focus:outline-none focus:border-brand-cyan transition-colors",
      })}
    </div>
  );
}
